import { ErrorBoundary, init } from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Provider } from 'react-redux'
import { Actions } from '~/actions'
import { CountryProvider, LanguageProvider, useTranslations } from '~/components/Locale'
import { ENV, filesUrl, imageUrl, includes, toGqlProduct } from '~/utils'
import configureStore from '../../configureStore'
import App from './App'

if (ENV().type !== 'development')
	init({
		dsn: 'https://3ed3af24447f493da4b252dc540e36b9@o1072187.ingest.sentry.io/6070567',

		release: import.meta.env.PACKAGE_VERSION,
		environment: ENV().type,
		tracesSampleRate: ENV().sampleRate,

		beforeBreadcrumb(breadcrumb, _hint) {
			if (breadcrumb.category === 'console' && includes(breadcrumb.level, 'info', 'log')) return null
			return breadcrumb
		}
	})

const store = configureStore()
const queryClient = new QueryClient()

const Fallback: React.FC = () => {
	const t = useTranslations('react')
	return createPortal(
		<div className="portal bg-white text-center text-2xl">
			<div className="m-auto max-w-3xl p-8">
				<h2>Error 500</h2>
				<img src={imageUrl(filesUrl('errordog.png'), '200x')} />
				<p>
					{t?.error_boundary.line_1}
					<br />
					{t?.error_boundary.line_2}
				</p>
				<p>{t?.error_boundary.retry}</p>
				<button className="btn btn--primary" onClick={_ => location.reload()}>
					{t?.actions.try_again}
				</button>
			</div>
		</div>,
		document.body
	)
}

const Root: React.FC = () => {
	useEffect(() => {
		if (window.PRODUCT) {
			const product = toGqlProduct(window.PRODUCT)
			store.dispatch(Actions.loadUpsells(product, product.variants[0]))
		}
		store.dispatch(Actions.loadDeliveryProfiles())
		store.dispatch(Actions.loadMarkets())
		store.dispatch(Actions.loadCart())
		console.info('Root app initialized.')
	}, [])

	return (
		<ErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<CountryProvider>
					<LanguageProvider>
						<ErrorBoundary fallback={<Fallback />} showDialog>
							<Provider store={store}>
								<React.StrictMode>
									<App />
								</React.StrictMode>
							</Provider>
						</ErrorBoundary>
					</LanguageProvider>
				</CountryProvider>
			</QueryClientProvider>
		</ErrorBoundary>
	)
}

export default Root
